import { useEffect, useState } from "react";
import { Box, Typography, Collapse } from "@mui/material";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useGetDelegationTask } from "services/delegation";
import Spinner from "components/common/Spinner";

type Props = {
  delegationTaskRef: string;
};

export default ({ delegationTaskRef }: Props) => {
  const { data, loading, request } = useGetDelegationTask();
  const [isExpanded, setIsExpanded] = useState(true);

  useEffect(() => {
    request({ ref: delegationTaskRef, markAsRead: false });
  }, [delegationTaskRef]);

  if (loading || !data?.delegationTask) {
    return (
      <Box display="flex" alignItems="center" gap="10px" padding="16px 0">
        <Spinner size={20} />
      </Box>
    );
  }

  const task = data.delegationTask;
  const details = task.detailsJson ? JSON.parse(task.detailsJson) : [];

  if (!task.description && details.length === 0) {
    return null;
  }

  return (
    <Box sx={{ display: "flex", gap: "16px" }}>
      <Box
        sx={{
          flex: 1,
          borderRadius: "16px",
          border: "1px solid #D4D4D4",
          padding: "16px 24px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: isExpanded ? "8px" : 0,
          }}
        >
          <Typography variant="h4">Task Details</Typography>
          <Box
            onClick={() => setIsExpanded(!isExpanded)}
            sx={{ cursor: "pointer", display: "flex" }}
          >
            {isExpanded ? (
              <ChevronUp size={20} stroke="#8E9598" />
            ) : (
              <ChevronDown size={20} stroke="#8E9598" />
            )}
          </Box>
        </Box>
        <Collapse in={isExpanded}>
          {task.description && (
            <Box sx={{ marginBottom: details.length > 0 ? "16px" : 0 }}>
              <Typography
                variant="bodySmall"
                color="text.secondary"
                sx={{ marginBottom: "4px" }}
              >
                Instructions
              </Typography>
              <Typography variant="body" sx={{ whiteSpace: "pre-wrap" }}>
                {task.description}
              </Typography>
            </Box>
          )}

          {details.length > 0 && (
            <Box>
              <Typography
                variant="bodySmall"
                color="text.secondary"
                sx={{ marginBottom: "8px" }}
              >
                Details
              </Typography>
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: "4px" }}
              >
                {details.map(
                  (detail: { name: string; value: string }, index: number) => (
                    <Box key={index} sx={{ display: "flex", gap: "8px" }}>
                      <Typography
                        variant="bodyHeavy"
                        sx={{ minWidth: "120px" }}
                      >
                        {detail.name}
                      </Typography>
                      <Typography
                        variant="body"
                        sx={{ whiteSpace: "pre-wrap" }}
                      >
                        {detail.value}
                      </Typography>
                    </Box>
                  ),
                )}
              </Box>
            </Box>
          )}
        </Collapse>
      </Box>
    </Box>
  );
};
